import { z } from 'zod';

/**
 * Hand-authored response schemas — TEMPORARY CONTRACT SOURCE.
 *
 * The backend OpenAPI currently declares request DTOs but NOT response shapes
 * (every response is an empty `200`). Until the backend annotates responses with
 * `@ApiOkResponse({ type: ... })`, these Zod schemas are the canonical source for
 * response validation and types on the frontend. They are derived from
 * `hostesa-backend/docs/frontend` + the domain model.
 *
 * CONTRACT GAP (backend follow-up): annotate API responses so these can be
 * replaced by generated types. See docs/adr/0002-contract-gap-responses.md.
 */

/**
 * A page of results plus the total matching the filters (ignoring limit/offset)
 * — mirrors the backend `Paginated<T>` envelope returned by the list endpoints.
 */
export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  });
}
export type Paginated<T> = { items: T[]; total: number; limit: number; offset: number };

export const roleSchema = z.enum([
  'hotel_admin',
  'hotel_staff',
  'hostesa_admin',
  'hostesa_support',
  'hostesa_readonly',
]);
export type Role = z.infer<typeof roleSchema>;

/** Operator partner-provisioning result: the new partner (tenant), its first hotel, and admin. */
export const provisionedPartnerSchema = z.object({
  tenant: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
  hotel: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
  adminUser: z.object({ id: z.string(), email: z.string(), fullName: z.string().nullable() }),
});
export type ProvisionedPartner = z.infer<typeof provisionedPartnerSchema>;

export const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  fullName: z.string().nullish(),
});
export type User = z.infer<typeof userSchema>;

/**
 * A membership grants a {@link Role} within a tenant, optionally scoped to a
 * single hotel. `hotelId: null` is a tenant-wide grant (all hotels under the
 * tenant, subject to role permissions).
 */
export const membershipSchema = z.object({
  tenantId: z.string(),
  tenantSlug: z.string().optional(),
  tenantName: z.string().optional(),
  hotelId: z.string().nullable(),
  hotelSlug: z.string().nullish(),
  hotelName: z.string().nullish(),
  role: roleSchema,
});
export type Membership = z.infer<typeof membershipSchema>;

export const loginResponseSchema = z.object({
  token: z.string(),
  user: userSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const meResponseSchema = z.object({
  user: userSchema,
  memberships: z.array(membershipSchema),
  impersonation: z
    .object({
      active: z.boolean(),
      byUserId: z.string(),
      label: z.string(),
    })
    .optional(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/** Stable error envelope: `{ error: { code, message } }` (see backend docs/05-errors). */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

// ---------------------------------------------------------------------------
// Canonical entity schemas
//
// These mirror the generated response DTOs in `gen/schema.ts`
// (`components['schemas']['*Dto']`) but exist as runtime Zod schemas so hooks can
// validate the wire response at the trust boundary. The generated TS types
// remain the compile-time contract; these add a runtime check.
//
// openapi-typescript emits `Record<string, never> | null` for nullable scalar
// fields the backend declares without an inner type — at runtime those carry
// real scalars, so here they are modelled as the permissive
// `nullableUnknown` (an actual value or null/undefined), never an empty object.
// ---------------------------------------------------------------------------

/** A field that may be a value, null, or absent (the DTO `... | null` shape). */
const nullableUnknown = z.unknown().nullish();
/** A nullable date-time string field. */
const dateTimeNullable = z.string().nullish();

// --- Calls ---
export const callStatusSchema = z.enum([
  'created',
  'ringing',
  'answered',
  'ai_active',
  'handoff_requested',
  'human_active',
  'completed',
  'failed',
]);
export type CallStatus = z.infer<typeof callStatusSchema>;

export const callSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  status: callStatusSchema,
  direction: z.enum(['inbound', 'outbound']),
  language: nullableUnknown,
  startedAt: dateTimeNullable,
  answeredAt: dateTimeNullable,
  endedAt: dateTimeNullable,
  durationSeconds: nullableUnknown,
  endReason: nullableUnknown,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Call = z.infer<typeof callSchema>;

export const transcriptSegmentSchema = z.object({
  id: z.string(),
  callId: z.string(),
  seq: z.number(),
  speaker: z.enum(['guest', 'agent', 'staff', 'system']),
  text: z.string(),
  language: nullableUnknown,
  confidence: nullableUnknown,
  startedAt: dateTimeNullable,
  endedAt: dateTimeNullable,
  createdAt: z.string(),
});
export type TranscriptSegment = z.infer<typeof transcriptSegmentSchema>;

export const callSummarySchema = z.object({
  id: z.string(),
  callId: z.string(),
  summary: z.string(),
  language: nullableUnknown,
  createdAt: z.string(),
});
export type CallSummary = z.infer<typeof callSummarySchema>;

/** Gateway decision outcome for an audited AI tool call. */
export const toolCallOutcomeSchema = z.enum([
  'executed',
  'denied',
  'failed',
  'idempotent',
  'in_flight',
]);
export type ToolCallOutcome = z.infer<typeof toolCallOutcomeSchema>;

/**
 * An AI Tool Gateway decision recorded during a call (e.g. `flag_handoff`,
 * `create_reservation_request`). Payload-free by design — no redacted tool
 * input/output is exposed, only the canonical name, outcome and timing.
 */
export const toolCallSchema = z.object({
  id: z.string(),
  name: z.string(),
  outcome: toolCallOutcomeSchema,
  riskLevel: nullableUnknown,
  occurredAt: z.string(),
});
export type ToolCall = z.infer<typeof toolCallSchema>;

export const handoffResponseSchema = z.object({
  callId: z.string(),
  status: callStatusSchema,
});
export type HandoffResponse = z.infer<typeof handoffResponseSchema>;

// --- Reservations & offers ---
export const reservationStatusSchema = z.enum([
  'new',
  'missing_information',
  'offer_needed',
  'offer_drafted',
  'offer_sent',
  'waiting_for_guest',
  'converted',
  'lost',
  'cancelled',
]);
export type ReservationStatus = z.infer<typeof reservationStatusSchema>;

export const reservationSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  callId: nullableUnknown,
  status: reservationStatusSchema,
  urgency: z.enum(['low', 'medium', 'high']),
  arrivalDate: nullableUnknown,
  departureDate: nullableUnknown,
  adults: nullableUnknown,
  children: nullableUnknown,
  childrenAges: z.array(z.number()).nullish(),
  roomTypePreference: nullableUnknown,
  boardPreference: nullableUnknown,
  contactLanguage: nullableUnknown,
  suggestedNextAction: nullableUnknown,
  missingFields: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Reservation = z.infer<typeof reservationSchema>;

export const offerDraftSchema = z.object({
  id: z.string(),
  reservationRequestId: z.string(),
  totalPrice: nullableUnknown,
  currency: z.string(),
  cancellationTerms: nullableUnknown,
  offerValidUntil: dateTimeNullable,
  status: z.enum(['drafted', 'sent', 'accepted', 'declined', 'expired']),
  sentAt: dateTimeNullable,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OfferDraft = z.infer<typeof offerDraftSchema>;

// --- Bookings ---
export const bookingStatusSchema = z.enum([
  'requested',
  'draft',
  'pending_staff_approval',
  'confirmed',
  'cancelled',
  'failed',
  'expired',
]);
export type BookingStatus = z.infer<typeof bookingStatusSchema>;

export const bookingSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  reservationRequestId: nullableUnknown,
  status: bookingStatusSchema,
  confirmationMode: z.enum(['manual', 'pms']),
  checkIn: nullableUnknown,
  checkOut: nullableUnknown,
  roomType: nullableUnknown,
  externalPmsId: nullableUnknown,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Booking = z.infer<typeof bookingSchema>;

// --- Knowledge ---
export const knowledgeCategorySchema = z.enum([
  'hotel_profile',
  'rooms',
  'offers',
  'policies',
  'services',
]);
export type KnowledgeCategory = z.infer<typeof knowledgeCategorySchema>;

export const knowledgeDocumentSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  category: knowledgeCategorySchema,
  language: z.string(),
  title: z.string(),
  content: z.string(),
  sourceType: z.enum(['website', 'pdf', 'manual', 'faq', 'knowledge_gap', 'pms']),
  sourceUrl: nullableUnknown,
  status: z.enum([
    'uploaded',
    'queued_for_parsing',
    'parsing',
    'parsed',
    'pending_review',
    'approved',
    'archived',
    'failed',
  ]),
  active: z.boolean(),
  visibility: z.string(),
  approvedBy: nullableUnknown,
  approvedAt: dateTimeNullable,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type KnowledgeDocument = z.infer<typeof knowledgeDocumentSchema>;

export const faqSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  category: knowledgeCategorySchema,
  language: z.string(),
  question: z.string(),
  approvedAnswer: z.string(),
  source: z.string(),
  sourceGapId: nullableUnknown,
  active: z.boolean(),
  approvedBy: nullableUnknown,
  approvedAt: dateTimeNullable,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Faq = z.infer<typeof faqSchema>;

export const knowledgeGapSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  callId: nullableUnknown,
  questionPreview: nullableUnknown,
  language: nullableUnknown,
  retrievalScore: nullableUnknown,
  status: z.enum(['needs_answer', 'answered', 'dismissed', 'duplicate']),
  resolvedFaqId: nullableUnknown,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type KnowledgeGap = z.infer<typeof knowledgeGapSchema>;

export const knowledgeSearchResultSchema = z.object({
  id: z.string(),
  content: z.string(),
  score: z.number().optional(),
});
export type KnowledgeSearchResult = z.infer<typeof knowledgeSearchResultSchema>;

// --- Scripts & voice config ---
export const hotelScriptSchema = z.object({
  id: z.string(),
  voiceConfigId: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  language: z.string(),
  field: z.string(),
  content: z.string(),
  status: z.enum(['draft', 'approved']),
  version: z.number(),
  approvedBy: nullableUnknown,
  approvedAt: dateTimeNullable,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type HotelScript = z.infer<typeof hotelScriptSchema>;

export const voiceConfigSchema = z.object({
  hotelId: z.string(),
  fallbackLanguage: z.string(),
  handoffRules: z.record(z.string(), z.unknown()),
  // CONTRACT GAP: the backend serializes `scripts` as a nested map
  // (`{ [language]: { [field]: { content, version } } }` of highest-version APPROVED content, also
  // consumed by the AI runtime), not the flat `HotelScript[]` the dashboard models here. The reading
  // hooks flatten the map into approved `HotelScript` rows before validation (see
  // `normalizeVoiceConfig`). Edits/version history go through `GET/PUT /scripts*` (`useScriptVersions`,
  // `useCreateOrUpdateScript`); this flattening only powers the Script Studio overview + status.
  scripts: z.array(hotelScriptSchema),
});
export type VoiceConfig = z.infer<typeof voiceConfigSchema>;

/**
 * Normalize a raw `/voice-config` body before validation. The backend serializes `scripts` as a
 * nested map (`{ [language]: { [field]: { content, version } } }`); flatten it in place into the
 * flat, approved `HotelScript[]` the dashboard models. Already-array payloads (tests/fixtures) pass
 * through untouched. Returns the same `raw` reference for convenient inlining.
 */
export function normalizeVoiceConfig(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const obj = raw as { scripts?: unknown };
  const map = obj.scripts;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return raw;
  const rows: unknown[] = [];
  for (const [language, fields] of Object.entries(map as Record<string, unknown>)) {
    if (!fields || typeof fields !== 'object') continue;
    for (const [field, entry] of Object.entries(fields as Record<string, unknown>)) {
      const e = (entry ?? {}) as { content?: unknown; version?: unknown };
      rows.push({
        id: `${language}:${field}`,
        voiceConfigId: '',
        tenantId: '',
        hotelId: '',
        language,
        field,
        content: typeof e.content === 'string' ? e.content : '',
        status: 'approved',
        version: typeof e.version === 'number' ? e.version : 1,
        approvedBy: null,
        approvedAt: null,
        createdAt: '',
        updatedAt: '',
      });
    }
  }
  obj.scripts = rows;
  return raw;
}

// --- Operations / in-stay tasks ---
export const serviceRequestStatusSchema = z.enum([
  'new',
  'assigned',
  'in_progress',
  'done',
  'cancelled',
  'escalated',
]);
export type ServiceRequestStatus = z.infer<typeof serviceRequestStatusSchema>;

export const serviceRequestSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  room: nullableUnknown,
  reservationId: nullableUnknown,
  category: z.enum([
    'housekeeping',
    'maintenance',
    'food_beverage',
    'concierge',
    'reception',
    'other',
  ]),
  requestType: z.string(),
  description: z.string(),
  urgency: z.enum(['low', 'medium', 'high']),
  requestedTime: dateTimeNullable,
  status: serviceRequestStatusSchema,
  assignedTeam: nullableUnknown,
  assignee: nullableUnknown,
  sourceChannel: z.enum(['voice', 'chat', 'whatsapp', 'email', 'manual']),
  sourceRef: z.string(),
  language: z.string(),
  acknowledgedAt: dateTimeNullable,
  completedAt: dateTimeNullable,
  slaDueAt: dateTimeNullable,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ServiceRequest = z.infer<typeof serviceRequestSchema>;

export const roomStatusSchema = z.object({
  roomId: z.string(),
  currentStatus: nullableUnknown,
  activeRequests: z.array(serviceRequestSchema),
});
export type RoomStatus = z.infer<typeof roomStatusSchema>;

// --- Onboarding ---
export const onboardingRunStatusSchema = z.enum([
  'created',
  'url_validated',
  'crawling',
  'draft_ready',
  'in_review',
  'approved',
  'indexing',
  'validating',
  'ready_for_test',
  'activated',
  'failed',
  'rejected',
  'expired',
]);
export type OnboardingRunStatus = z.infer<typeof onboardingRunStatusSchema>;

export const onboardingRunSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  domain: nullableUnknown,
  urlProvided: z.string(),
  status: onboardingRunStatusSchema,
  statusDetail: nullableUnknown,
  domainOwnershipConfirmed: z.boolean(),
  pagesQueued: z.number(),
  pagesFetched: z.number(),
  itemsExtracted: z.number(),
  itemsReviewed: z.number(),
  itemsApproved: z.number(),
  createdBy: nullableUnknown,
  approvedBy: nullableUnknown,
  approvedAt: dateTimeNullable,
  activatedAt: dateTimeNullable,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OnboardingRun = z.infer<typeof onboardingRunSchema>;

export const onboardingDraftItemSchema = z.object({
  id: z.string(),
  onboardingRunId: z.string(),
  itemType: z.enum(['profile', 'faq', 'document']),
  itemCategory: nullableUnknown,
  sourceUrls: z.array(z.string()),
  extractedValue: nullableUnknown,
  extractedConfidence: nullableUnknown,
  reviewedValue: nullableUnknown,
  status: z.enum(['pending_review', 'accepted', 'edited', 'rejected']),
  requiredField: z.boolean(),
  reviewReason: nullableUnknown,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OnboardingDraftItem = z.infer<typeof onboardingDraftItemSchema>;

export const validateUrlResultSchema = z.object({
  isValid: z.boolean(),
  domain: z.string().optional(),
});
export type ValidateUrlResult = z.infer<typeof validateUrlResultSchema>;

export const activationGateSchema = z.object({
  requiredFieldsConfirmed: z.boolean(),
  retrievalSmokePassed: z.boolean(),
  syntheticCallPassed: z.boolean(),
});
export type ActivationGate = z.infer<typeof activationGateSchema>;

// --- Phone ---
export const phoneNumberSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  phoneNumber: z.string(),
  voiceProvider: z.string(),
  providerNumberId: nullableUnknown,
  forwardingEnabled: z.boolean(),
  status: z.enum(['active', 'inactive']),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PhoneNumber = z.infer<typeof phoneNumberSchema>;

export const phoneForwardingRuleSchema = z.object({
  id: z.string(),
  phoneNumberId: z.string(),
  forwardingCondition: z.enum(['busy', 'no_answer', 'after_hours', 'always']),
  forwardingTarget: z.string(),
  enabled: z.boolean(),
  noAnswerTimeoutSeconds: nullableUnknown,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PhoneForwardingRule = z.infer<typeof phoneForwardingRuleSchema>;

export const workingHoursSchema = z.object({
  id: z.string(),
  dayOfWeek: z.number(),
  startTime: nullableUnknown,
  endTime: nullableUnknown,
  isOpen: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WorkingHours = z.infer<typeof workingHoursSchema>;

export const phoneNumberConfigSchema = z.object({
  phoneNumber: phoneNumberSchema,
  forwardingRules: z.array(phoneForwardingRuleSchema),
  workingHours: z.array(workingHoursSchema),
});
export type PhoneNumberConfig = z.infer<typeof phoneNumberConfigSchema>;

// --- Hotels ---
export const hotelSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  slug: z.string(),
  name: z.string(),
  timezone: z.string(),
  defaultLanguage: z.string(),
  languages: z.array(z.string()),
  status: z.enum(['draft', 'active', 'suspended']),
  operationsAgentEnabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Hotel = z.infer<typeof hotelSchema>;

// --- Hotel setup facts ---
// One curated operational fact per (hotel, category). The `category` is one of
// the backend's ALLOWED_FACT_CATEGORIES; the UI badges the subset the activation
// gate requires (see `requiredCategories` below).
export const hotelFactSchema = z.object({
  id: z.string(),
  category: z.string(),
  value: z.string(),
  language: z.string(),
  updatedAt: z.string(),
});
export type HotelFact = z.infer<typeof hotelFactSchema>;

export const hotelFactsResponseSchema = z.object({
  facts: z.array(hotelFactSchema),
  requiredCategories: z.array(z.string()),
  allowedCategories: z.array(z.string()),
});
export type HotelFactsResponse = z.infer<typeof hotelFactsResponseSchema>;

// --- Members ---
export const memberSchema = z.object({
  userId: z.string(),
  email: nullableUnknown,
  fullName: nullableUnknown,
  status: z.enum(['active', 'suspended']).nullish(),
  role: z.enum(['hotel_admin', 'hotel_staff', 'tenant_admin']),
  hotelId: nullableUnknown,
});
export type Member = z.infer<typeof memberSchema>;

export const membershipRowSchema = z.object({
  id: z.string(),
  userId: z.string(),
  tenantId: z.string(),
  hotelId: nullableUnknown,
  role: z.enum(['hotel_admin', 'hotel_staff', 'tenant_admin']),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MembershipRow = z.infer<typeof membershipRowSchema>;

export const inviteMemberResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    fullName: nullableUnknown,
    status: z.enum(['active', 'suspended']),
  }),
  membership: membershipRowSchema,
});
export type InviteMemberResponse = z.infer<typeof inviteMemberResponseSchema>;

// --- Integrations ---
export const integrationSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  provider: z.string(),
  status: z.enum([
    'not_configured',
    'configured',
    'testing',
    'active',
    'degraded',
    'auth_failed',
    'disabled',
  ]),
  capabilityMatrix: z.record(z.string(), z.unknown()),
  syncState: z.record(z.string(), z.unknown()),
  lastTestedAt: dateTimeNullable,
  errorDetail: nullableUnknown,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Integration = z.infer<typeof integrationSchema>;

export const integrationCapabilitiesSchema = z.object({
  capabilities: z.record(z.string(), z.unknown()),
});
export type IntegrationCapabilities = z.infer<typeof integrationCapabilitiesSchema>;

export const testConnectionResultSchema = z.object({
  success: z.boolean(),
  error: nullableUnknown,
});
export type TestConnectionResult = z.infer<typeof testConnectionResultSchema>;

// --- Usage & cost ---
export const usageEntrySchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: nullableUnknown,
  callId: nullableUnknown,
  usageType: z.string(),
  provider: nullableUnknown,
  quantity: z.string(),
  unitCost: z.string(),
  totalCost: z.string(),
  currency: z.string(),
  createdAt: z.string(),
});
export type UsageEntry = z.infer<typeof usageEntrySchema>;

export const callCostSchema = z.object({
  callId: z.string(),
  totalCost: z.string(),
  currency: z.string(),
});
export type CallCost = z.infer<typeof callCostSchema>;

// --- Operator (cross-tenant directories) ---
//
// These mirror the backend operator DTOs (`TenantDto`, `OperatorHotelDto`,
// `OperatorCallDto`). They are operator-only (gated by `admin:cross_tenant_read`)
// and carry NO transcript/guest content — the call directory is diagnostics only.

export const tenantPlanSchema = z.enum(['starter', 'growth', 'pro']);
export type TenantPlan = z.infer<typeof tenantPlanSchema>;

export const tenantStatusSchema = z.enum(['active', 'suspended', 'cancelled']);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

/** A partner (tenant) row in the operator cross-tenant directory. */
export const operatorTenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  plan: tenantPlanSchema,
  status: tenantStatusSchema,
  hotelsCount: z.number(),
  calls24h: z.number(),
  createdAt: z.string(),
});
export type OperatorTenant = z.infer<typeof operatorTenantSchema>;

/** A hotel row in the operator directory, enriched with its owning partner. */
export const operatorHotelSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  slug: z.string(),
  timezone: z.string(),
  status: z.enum(['draft', 'active', 'suspended']),
  tenantName: z.string().nullable(),
  tenantSlug: z.string().nullable(),
  calls24h: z.number(),
  // REAL phone-line counts (active / total) from the `phone_numbers` table.
  // Optional for deploy-skew tolerance: a frontend shipped ahead of the backend
  // (older API not yet returning these) still parses — the UI renders "—".
  linesActive: z.number().optional(),
  linesTotal: z.number().optional(),
  createdAt: z.string(),
});
export type OperatorHotel = z.infer<typeof operatorHotelSchema>;

/**
 * A call row in the operator directory. A trimmed projection of a call (no
 * transcript/guest content) enriched with partner + hotel slugs for context.
 */
export const operatorCallSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  status: callStatusSchema,
  direction: z.enum(['inbound', 'outbound']),
  language: nullableUnknown,
  startedAt: dateTimeNullable,
  durationSeconds: nullableUnknown,
  createdAt: z.string(),
  tenantSlug: z.string().nullable(),
  hotelSlug: z.string().nullable(),
});
export type OperatorCall = z.infer<typeof operatorCallSchema>;

// --- Operator members & impersonation ---

/** A member row in the operator tenant-members directory. */
export const operatorMemberSchema = z.object({
  userId: z.string(),
  email: z.string().nullish(),
  fullName: z.string().nullish(),
  role: z.string(),
  hotelId: z.string().nullable(),
});
export type OperatorMember = z.infer<typeof operatorMemberSchema>;

/** User account status (REAL field — the `users` table has a `status` column). */
export const operatorUserStatusSchema = z.enum(['invited', 'active', 'disabled']);
export type OperatorUserStatus = z.infer<typeof operatorUserStatusSchema>;

/**
 * A user row in the operator cross-tenant directory (`GET /operator/users`).
 *
 * Honesty notes baked into the shape: `email` is the REAL address (mask it
 * client-side in the UI); `status` is a REAL account-status field. `role` is
 * the primary membership role and is null for membership-less users
 * (`hostesa_admin` = operator). Operators are deliberately NOT partner-scoped,
 * so their `tenantId`/`tenantName`/`tenantSlug` come back null. `mfaEnabled` is
 * a REAL flag from the `users` table; there are still NO last-seen / phone
 * fields — none exist server-side, so none are invented.
 */
export const operatorUserSchema = z.object({
  userId: z.string(),
  email: z.string(),
  fullName: z.string().nullable(),
  role: roleSchema.nullable(),
  status: operatorUserStatusSchema,
  // REAL MFA-enabled flag from the `users` table. Optional for deploy-skew
  // tolerance (older API not yet returning it); the UI renders "—" when absent.
  mfaEnabled: z.boolean().optional(),
  tenantId: z.string().nullable(),
  tenantName: z.string().nullable(),
  tenantSlug: z.string().nullable(),
  createdAt: z.string(),
});
export type OperatorUser = z.infer<typeof operatorUserSchema>;

/** Result of starting an operator impersonation session. */
export const impersonationResultSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
  target: operatorMemberSchema,
  label: z.string(),
  // Echoed back (normalised) so the backoffice can confirm what it sent.
  reason: z.string(),
  supportTicketId: z.string(),
});
export type ImpersonationResult = z.infer<typeof impersonationResultSchema>;

// --- Audit ---
export const auditLogSchema = z.object({
  id: z.string(),
  tenantId: nullableUnknown,
  hotelId: nullableUnknown,
  actorType: z.string(),
  actorId: nullableUnknown,
  action: z.string(),
  resourceType: nullableUnknown,
  resourceId: nullableUnknown,
  // Backend outcomes are open-ended (e.g. 'executed', 'success', 'blocked'),
  // not a fixed 3-value enum — keep this permissive to avoid rejecting rows.
  outcome: z.string(),
  riskLevel: nullableUnknown,
  traceId: nullableUnknown,
  correlationId: nullableUnknown,
  inputRedacted: z.record(z.string(), z.unknown()).nullish(),
  outputRedacted: nullableUnknown,
  createdAt: z.string(),
});
export type AuditLog = z.infer<typeof auditLogSchema>;
